import { useCallback, useEffect, useMemo, useState } from 'react';

import { Empty, Tree } from 'antd';
import type { AntTreeNodeProps, DataNode } from 'antd/es/tree';

import { COLOR } from '@/shared/design-tokens/design-tokens';
import { useSearchLengthValidation } from '@/shared/hooks/useSearchLengthValidation';
import { EMPTY_MESSAGES, MODAL_WIDTHS } from '@/shared/lib/constants';
import { highlightText } from '@/shared/lib/text';
import { BaseModal } from '@/shared/ui/BaseModal';
import { Chip } from '@/shared/ui/Chip';
import type { SearchValidation } from '@/shared/ui/EditableTreeSelect/types';
import { Icon } from '@/shared/ui/Icon';
import { SearchInput } from '@/shared/ui/SearchInput';

import styles from './MetadataEditModal.module.css';
import type { TreeSelectNode } from './type';

type MetadataEditModalProps = {
  open: boolean;
  title?: string;
  icon?: React.ReactNode;
  firstLabel: string;
  secondLabel: string;
  placeholder?: string;
  searchValidation?: SearchValidation;
  treeSelectValue: { value: string; label: string }[];
  checkStrictly?: boolean;
  loading?: boolean;
  treeData: TreeSelectNode[];
  onClose: () => void;
  onSubmit: (newTagFQNs: string[]) => void;
};

const convertToDataNode = (nodes: TreeSelectNode[], searchTerm: string): DataNode[] => {
  return nodes.map((node) => {
    const nodeTitle = typeof node.title === 'string' ? node.title : node.label || '';

    const titleElement = searchTerm ? (
      <span className={styles.nodeLabel}>{highlightText(nodeTitle, searchTerm, { className: 'highlight' })}</span>
    ) : (
      <span className={styles.nodeLabel}>{nodeTitle}</span>
    );

    return {
      title: titleElement,
      key: node.value,
      children: node.children ? convertToDataNode(node.children, searchTerm) : undefined,
      disabled: node.disabled,
    };
  });
};

const findNodeByValue = (nodes: TreeSelectNode[], value: string): TreeSelectNode | null => {
  for (const node of nodes) {
    if (node.value === value) return node;
    if (node.children) {
      const found = findNodeByValue(node.children, value);
      if (found) return found;
    }
  }
  return null;
};

const switcherIcon = (nodeProps: AntTreeNodeProps) => {
  const { expanded } = nodeProps;
  return expanded ? (
    <Icon name="tree-collapse" size={16} color={COLOR.GRAY[0]} />
  ) : (
    <Icon name="tree-expand" size={16} color={COLOR.GRAY[0]} />
  );
};

const MetadataEditModal = ({
  open,
  icon,
  title = '메타데이터 편집',
  firstLabel = '선택된 태그',
  secondLabel = '태그 편집',
  placeholder = '태그를 검색하세요',
  searchValidation,
  treeData,
  treeSelectValue,
  checkStrictly = false,
  onClose,
  onSubmit,
  loading = false,
}: MetadataEditModalProps) => {
  const [selectedChipValues, setSelectedChipValues] = useState<{ value: string; label: string }[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

  const { validateLength, handleCompositionStart, handleCompositionEnd } = useSearchLengthValidation({
    maxLength: searchValidation?.maxLength || 999999,
    warningMessage: searchValidation?.warningMessage || '',
    debounceMs: 1000,
  });

  const filteredTreeData = useMemo(() => {
    if (!searchQuery.trim()) return treeData;

    const filterTree = (nodes: TreeSelectNode[]): TreeSelectNode[] => {
      return nodes
        .map((node) => {
          const nodeTitle = typeof node.title === 'string' ? node.title : node.label;
          const matchesTitle = nodeTitle && nodeTitle.toLowerCase().includes(searchQuery.toLowerCase());
          const filteredChildren = node.children ? filterTree(node.children) : [];

          if (matchesTitle || filteredChildren.length > 0) {
            return {
              ...node,
              children: filteredChildren,
            };
          }
          return null;
        })
        .filter(Boolean) as TreeSelectNode[];
    };

    return filterTree(treeData);
  }, [searchQuery, treeData]);

  const convertedTreeData = useMemo(
    () => convertToDataNode(filteredTreeData, searchQuery),
    [filteredTreeData, searchQuery]
  );

  const checkedKeys = useMemo(() => selectedChipValues.map((tag) => tag.value), [selectedChipValues]);

  const handleCloseChip = useCallback((value: string) => {
    setSelectedChipValues((prev) => prev.filter((tag) => tag.value !== value));
  }, []);

  const handleCheck = useCallback(
    (checkedKeysValue: React.Key[] | { checked: React.Key[]; halfChecked: React.Key[] }) => {
      const keys = Array.isArray(checkedKeysValue) ? checkedKeysValue : checkedKeysValue.checked;
      const stringKeys = keys.map((key) => String(key));

      const newSelectedChipValues = stringKeys.map((key) => {
        const node = findNodeByValue(treeData, key);
        return {
          value: key,
          label: typeof node?.title === 'string' ? node.title : node?.label || key,
        };
      });

      setSelectedChipValues(newSelectedChipValues);
    },
    [treeData]
  );

  const handleExpand = useCallback((keys: React.Key[]) => {
    setExpandedKeys(keys);
  }, []);

  const handleSubmit = useCallback(async () => {
    const newTagFQNs = selectedChipValues.map((tag) => tag.value);
    await onSubmit(newTagFQNs);
    onClose();
  }, [onClose, onSubmit, selectedChipValues]);

  useEffect(() => {
    if (open) {
      setSelectedChipValues(treeSelectValue);
      setExpandedKeys(treeData.map((node) => node.value));
      setSearchQuery('');
    }
  }, [open, treeSelectValue, treeData]);

  return (
    <BaseModal
      title={title}
      open={open}
      confirmLoading={loading}
      okText="업데이트"
      onClose={onClose}
      onOk={handleSubmit}
      width={MODAL_WIDTHS.STANDARD_FORM}>
      <div className={styles.content}>
        <div className={styles.container}>
          <div className={styles.selectedTagsTitle}>{firstLabel}</div>
          <div className={styles.selectedTagsList}>
            {selectedChipValues.length > 0 ? (
              selectedChipValues.map((tag) => {
                return (
                  <Chip key={tag.value} icon={icon} label={tag.label} onClose={() => handleCloseChip(tag.value)} />
                );
              })
            ) : (
              <span>-</span>
            )}
          </div>
        </div>
        <div className={styles.container}>
          <div className={styles.selectedTagsTitle}>{secondLabel}</div>
          <SearchInput
            placeholder={placeholder}
            className={styles.searchInput}
            value={searchQuery}
            onChange={(e) => {
              const validatedValue = searchValidation ? validateLength(e.target.value) : e.target.value;
              setSearchQuery(validatedValue);
            }}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            maxLength={searchValidation?.maxLength}
          />
          <div className={styles.treeSelectContainer}>
            {convertedTreeData.length > 0 ? (
              <Tree
                checkable
                expandedKeys={expandedKeys}
                checkStrictly={checkStrictly}
                checkedKeys={checkedKeys}
                onCheck={handleCheck}
                treeData={convertedTreeData}
                switcherIcon={switcherIcon}
                className={styles.tree}
                onExpand={handleExpand}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={EMPTY_MESSAGES.NO_SEARCH_RESULTS}
                className={styles.emptyState}
              />
            )}
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export { MetadataEditModal };
export type { MetadataEditModalProps };
